import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, getCurrentUser } from '../utils/api';
import Odontogram from './Odontogram';
import PatientRecord from './PatientRecord';
import {
  Users, Clock, AlertTriangle, Phone, User,
  Play, CheckCircle, RefreshCw, ChevronLeft,
  AlertCircle, FileText, Calendar, Activity,
  UserPlus, Search, X, CreditCard
} from 'lucide-react';

const REFRESH_INTERVAL = 30000; // 30 seconds

const WaitingList = () => {
  const { print_lang, currentLanguage } = useLanguage();
  const user = getCurrentUser();

  // Queue data
  const [queue, setQueue] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Selected patient for treatment
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showRecord, setShowRecord] = useState(false);

  // Treatment modal
  const [showTreatmentModal, setShowTreatmentModal] = useState(false);
  const [treatments, setTreatments] = useState([]);
  const [selectedTreatments, setSelectedTreatments] = useState([]);
  const [treatmentNotes, setTreatmentNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Walk-in modal
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [patients, setPatients] = useState([]);
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [walkInForm, setWalkInForm] = useState({
    patient_id: '',
    evtype_id: '',
    notes: ''
  });
  const [isCreatingWalkIn, setIsCreatingWalkIn] = useState(false);

  // New patient registration in walk-in
  const [showNewPatientForm, setShowNewPatientForm] = useState(false);
  const [newPatientForm, setNewPatientForm] = useState({
    name: '',
    phone: '',
    citizen_id: '',
    gender: '',
    dob: '',
    address: '',
    drug_allergy: '',
    medical_history: ''
  });
  const [isCreatingPatient, setIsCreatingPatient] = useState(false);
  const [isReadingCard, setIsReadingCard] = useState(false);
  const [branchSettings, setBranchSettings] = useState(null);

  // In-page notice, replacing this screen's native alert() calls. A native
  // dialog on a tablet blocks the whole app behind an OS chrome the dentist
  // cannot style, size or reach one-handed, and it fires on the core
  // queue -> treat -> finish path. Same idea as PatientRecord's banner, but
  // fixed-position: several of these fire while a modal is open, and a banner
  // in the page body would be painted underneath it.
  const [notice, setNotice] = useState(null); // { type: 'error' | 'success', text }
  const showError = useCallback((text) => setNotice({ type: 'error', text }), []);
  const showSuccess = useCallback((text) => setNotice({ type: 'success', text }), []);

  // A confirmation is only worth reading once; a success message is not, and
  // leaving it over the queue would hide the next patient's row.
  useEffect(() => {
    if (notice?.type !== 'success') return undefined;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  // Set by the Odontogram whenever the chart holds marks Save has not written.
  // Navigating away from the treatment screen unmounts the chart and discards
  // them, so all three exits from that screen check this first: opening the
  // clinical record, going back to the queue, and finishing the treatment.
  const [odontogramDirty, setOdontogramDirty] = useState(false);
  const handleOdontogramDirty = useCallback((dirty) => setOdontogramDirty(dirty), []);
  // The navigation the dentist asked for, held back until they confirm:
  // 'record' (open the clinical record), 'finish' (start the finish-treatment
  // flow) or 'back' (leave the patient).
  const [pendingNav, setPendingNav] = useState(null);

  // Fetch queue data
  const fetchQueue = useCallback(async () => {
    // console.log('=== WaitingList fetchQueue ===');
    // console.log('Current user - dentist_id:', user?.dentist_id, 'branch_id:', user?.branch_id);

    if (!user?.dentist_id || !user?.branch_id) {
      // console.log('Missing user data - cannot fetch queue');
      return;
    }

    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.append('branch_id', user.branch_id);
      // Note: Removed dentist_id filter to show ALL appointments for the branch
      // This allows dentist to see all patients in queue, not just their own
      // params.append('dentist_id', user.dentist_id);

      // console.log('Fetching queue with params:', params.toString());

      const response = await fetchWithAuth(`/api/appointments/today/waiting?${params}`);
      const queueData = response.data || response || [];
      // console.log('Queue data received:', queueData.length, 'appointments for branch_id:', user.branch_id);
      setQueue(queueData);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.dentist_id, user?.branch_id]);

  // Fetch treatments for modal
  const fetchTreatments = async () => {
    try {
      // console.log('Fetching treatments, user:', user);
      const response = await fetchWithAuth('/api/treatments?status=active');
      // console.log('Treatments API response:', response);
      const treatmentList = response.data || response || [];
      // console.log('Treatment list length:', treatmentList.length);
      setTreatments(treatmentList);
    } catch (error) {
      console.error('Failed to fetch treatments:', error);
    }
  };

  // Fetch patients for walk-in
  const fetchPatients = async (search = '') => {
    if (!user?.branch_id) return;
    try {
      const params = new URLSearchParams({
        branch_id: user.branch_id,
        status: 'active'
      });
      if (search) params.append('search', search);
      const response = await fetchWithAuth(`/api/patients?${params}`);
      setPatients(response.data || response || []);
    } catch (error) {
      console.error('Failed to fetch patients:', error);
    }
  };

  // Fetch appointment types
  const fetchAppointmentTypes = async () => {
    try {
      const data = await fetchWithAuth('/api/appointments/types/all');
      setAppointmentTypes((data || []).filter(t => t.type_name !== 'Day Off'));
    } catch (error) {
      console.error('Failed to fetch appointment types:', error);
    }
  };

  // Open walk-in modal
  const openWalkInModal = () => {
    fetchPatients();
    fetchAppointmentTypes();
    setShowWalkInModal(true);
  };

  // Close walk-in modal
  const closeWalkInModal = () => {
    setShowWalkInModal(false);
    setShowNewPatientForm(false);
    setPatientSearch('');
    setWalkInForm({ patient_id: '', evtype_id: '', notes: '' });
    setNewPatientForm({
      name: '',
      phone: '',
      citizen_id: '',
      gender: '',
      dob: '',
      address: '',
      drug_allergy: '',
      medical_history: ''
    });
  };

  // Fetch branch settings for smartcard
  const fetchBranchSettings = async () => {
    if (!user?.branch_id) return;
    try {
      const data = await fetchWithAuth(`/api/branches/${user.branch_id}`);
      setBranchSettings(data);
    } catch (error) {
      console.error('Failed to fetch branch settings:', error);
    }
  };

  // SmartCard Helper Functions
  const convertBuddhistDate = (dateStr) => {
    if (!dateStr) return '';
    const cleanDate = dateStr.toString().replace(/[\s-]/g, '');
    let year, month, day;

    if (cleanDate.includes('/')) {
      const parts = cleanDate.split('/');
      day = parseInt(parts[0]);
      month = parseInt(parts[1]);
      year = parseInt(parts[2]) - 543;
    } else if (cleanDate.length === 8) {
      year = parseInt(cleanDate.substring(0, 4)) - 543;
      month = parseInt(cleanDate.substring(4, 6));
      day = parseInt(cleanDate.substring(6, 8));
    } else {
      return '';
    }

    if (isNaN(year) || isNaN(month) || isNaN(day)) return '';
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const extractPrefixAndName = (fullName) => {
    const prefixes = ['นาย', 'นาง', 'นางสาว', 'เด็กชาย', 'เด็กหญิง', 'ด.ช.', 'ด.ญ.'];
    for (const prefix of prefixes) {
      if (fullName.startsWith(prefix)) {
        return { prefix, name: fullName.substring(prefix.length).trim() };
      }
    }
    return { prefix: '', name: fullName.trim() };
  };

  // Read SmartCard
  const handleReadSmartcard = async () => {
    setIsReadingCard(true);
    try {
      const response = await fetch('http://localhost:8081/json');
      if (!response.ok) {
        throw new Error('Failed to read smartcard');
      }

      const data = await response.json();
      // console.log('Smartcard data:', data);

      const { name } = extractPrefixAndName(data.NameTH || '');

      let gender = '';
      if (data.Sex === 'ชาย') gender = 'male';
      else if (data.Sex === 'หญิง') gender = 'female';

      const dob = convertBuddhistDate(data.DoB);

      setNewPatientForm(prev => ({
        ...prev,
        citizen_id: data.CitizenID || '',
        name: name,
        gender: gender,
        dob: dob,
        address: (data.Address || '').trim()
      }));

    } catch (error) {
      console.error('Failed to read smartcard:', error);
      showError(print_lang('smartcard_error', 'Failed to read smartcard. Please make sure the card reader is connected and the card is inserted.'));
    } finally {
      setIsReadingCard(false);
    }
  };

  // Create new patient
  const createNewPatient = async () => {
    if (!newPatientForm.name.trim()) {
      showError(print_lang('name_required', 'Patient name is required'));
      return;
    }

    setIsCreatingPatient(true);
    try {
      const response = await fetchWithAuth('/api/patients', {
        method: 'POST',
        body: JSON.stringify({
          branch_id: user.branch_id,
          name: newPatientForm.name.trim(),
          phone: newPatientForm.phone.trim(),
          citizen_id: newPatientForm.citizen_id.trim(),
          gender: newPatientForm.gender,
          dob: newPatientForm.dob || null,
          address: newPatientForm.address.trim(),
          drug_allergy: newPatientForm.drug_allergy.trim(),
          medical_history: newPatientForm.medical_history.trim(),
          status: 'active'
        })
      });

      const newPatientId = response.patient_id || response.id;
      if (newPatientId) {
        await fetchPatients();
        setWalkInForm(prev => ({ ...prev, patient_id: String(newPatientId) }));
        setShowNewPatientForm(false);
        setNewPatientForm({
          name: '',
          phone: '',
          citizen_id: '',
          gender: '',
          dob: '',
          address: '',
          drug_allergy: '',
          medical_history: ''
        });
      }
    } catch (error) {
      console.error('Failed to create patient:', error);
      showError(error.message || print_lang('error_creating_patient', 'Failed to create patient'));
    } finally {
      setIsCreatingPatient(false);
    }
  };

  // Create walk-in appointment
  const createWalkInAppointment = async () => {
    if (!walkInForm.patient_id) {
      showError(print_lang('select_patient_required', 'Please select a patient'));
      return;
    }

    setIsCreatingWalkIn(true);
    try {
      const now = new Date();
      const endTime = new Date(now.getTime() + 60 * 60 * 1000);
      const selectedPatientData = patients.find(p => p.patient_id === parseInt(walkInForm.patient_id));

      await fetchWithAuth('/api/appointments', {
        method: 'POST',
        body: JSON.stringify({
          branch_id: user.branch_id,
          patient_id: walkInForm.patient_id,
          dentist_id: user.dentist_id,
          evtype_id: walkInForm.evtype_id || null,
          text: walkInForm.notes || `Walk-in: ${selectedPatientData?.name || 'Patient'}`,
          start_date: now.toISOString(),
          end_date: endTime.toISOString(),
          status: 'checked_in'
        })
      });

      closeWalkInModal();
      fetchQueue();
      showSuccess(print_lang('walkin_created', 'Walk-in patient added to queue'));
    } catch (error) {
      console.error('Failed to create walk-in:', error);
      showError(error.message || print_lang('error_creating_walkin', 'Failed to create walk-in appointment'));
    } finally {
      setIsCreatingWalkIn(false);
    }
  };

  // Initial load and refresh interval
  useEffect(() => {
    fetchQueue();
    fetchTreatments();
    fetchBranchSettings();

    const interval = setInterval(fetchQueue, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchQueue]);

  // Update appointment status
  const updateStatus = async (appointmentId, status) => {
    try {
      await fetchWithAuth(`/api/appointments/${appointmentId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });
      fetchQueue();
    } catch (error) {
      console.error('Failed to update status:', error);
      // Prefer the translated string; the server's raw English message is the
      // fallback, same order as everywhere else on this branch.
      showError(print_lang('status_update_failed', 'Could not update the appointment') + (error.message ? ': ' + error.message : ''));
    }
  };

  // Handle start treatment
  const handleStartTreatment = async (appointment) => {
    if (appointment.status !== 'in_progress') {
      await updateStatus(appointment.id, 'in_progress');
    }

    setSelectedAppointment(appointment);
    setSelectedPatient({
      patient_id: appointment.patient_id,
      name: appointment.patient_name,
      phone: appointment.patient_phone,
      citizen_id: appointment.citizen_id,
      drug_allergy: appointment.drug_allergy,
      medical_history: appointment.medical_history
    });
  };

  // Handle finish treatment
  const handleFinishTreatment = async () => {
    if (selectedTreatments.length === 0) {
      showError(print_lang('select_at_least_one_treatment', 'Please select at least one treatment'));
      return;
    }

    setIsSaving(true);
    try {
      // Create treatment records
      for (const treatment of selectedTreatments) {
        await fetchWithAuth('/api/treatment-records', {
          method: 'POST',
          body: JSON.stringify({
            patient_id: selectedAppointment.patient_id,
            treatment_id: treatment.t_id,
            dentist_id: user?.dentist_id,
            date: new Date().toISOString().split('T')[0],
            notes: treatmentNotes,
            cost: treatment.price,
            status: 'completed'
          })
        });
      }

      // Update appointment to completed
      await updateStatus(selectedAppointment.id, 'completed');

      // Reset state
      setShowTreatmentModal(false);
      setSelectedTreatments([]);
      setTreatmentNotes('');
      setSelectedAppointment(null);
      setSelectedPatient(null);

      showSuccess(print_lang('treatment_completed', 'Treatment completed and sent to billing'));
    } catch (error) {
      console.error('Failed to complete treatment:', error);
      showError(print_lang('finish_treatment_failed', 'Could not finish the treatment') + (error.message ? ': ' + error.message : ''));
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle treatment selection
  const toggleTreatment = (treatment) => {
    setSelectedTreatments(prev => {
      const exists = prev.find(t => t.t_id === treatment.t_id);
      if (exists) {
        return prev.filter(t => t.t_id !== treatment.t_id);
      }
      return [...prev, treatment];
    });
  };

  // Format time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge styles
  const getStatusBadge = (status) => {
    const styles = {
      scheduled: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Calendar },
      checked_in: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Activity },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle }
    };
    return styles[status] || styles.scheduled;
  };

  // Queue statistics
  const stats = {
    waiting: queue.filter(a => a.status === 'checked_in').length,
    inProgress: queue.filter(a => a.status === 'in_progress').length,
    scheduled: queue.filter(a => a.status === 'scheduled').length,
    total: queue.length
  };

  // Both exits from the treatment screen unmount the Odontogram, and the chart
  // holds every mark in local state until Save. Ask first rather than throwing
  // the marks away silently -- in the page, not through window.confirm, which
  // is the same native dialog this branch removed everywhere else.
  const runNav = (target) => {
    if (target === 'record') {
      setShowRecord(true);
    } else if (target === 'finish') {
      // Finishing is the third way off this screen, and the likeliest one at
      // the end of a visit: handleFinishTreatment clears selectedPatient, which
      // unmounts the chart. Ask here, before the treatment modal, so the
      // dentist can still go back and Save rather than after the records are
      // already written.
      setShowTreatmentModal(true);
    } else {
      setSelectedPatient(null);
      setSelectedAppointment(null);
    }
  };

  const requestNav = (target) => {
    if (odontogramDirty) {
      setPendingNav(target);
      return;
    }
    runNav(target);
  };

  // Rendered inside each return below rather than around them: this component
  // has three separate render paths, and the two that raise notices are the
  // queue and the treatment screen.
  const noticeBanner = notice && (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[100001] w-[92vw] max-w-xl px-1">
      <div className={`flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg border ${
        notice.type === 'success'
          ? 'bg-green-50 border-green-300 text-green-800'
          : 'bg-red-50 border-red-300 text-red-800'
      }`}>
        {notice.type === 'success' ? <CheckCircle size={20} className="mt-0.5 shrink-0" />
                                   : <AlertTriangle size={20} className="mt-0.5 shrink-0" />}
        <span className="text-sm flex-1">{notice.text}</span>
        <button onClick={() => setNotice(null)}
                aria-label={print_lang('close', 'Close')}
                className="min-w-[44px] min-h-[44px] -my-2 flex items-center justify-center text-lg leading-none">
          &times;
        </button>
      </div>
    </div>
  );

  const unsavedChartDialog = pendingNav && (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setPendingNav(null); }}>
      <div className="modal-content w-full max-w-md p-6">
        <h3 className="text-lg font-bold flex items-center gap-2 mb-3">
          <AlertTriangle size={22} className="text-amber-600" />
          {print_lang('unsaved_chart_changes', 'Unsaved tooth chart')}
        </h3>
        <p className="text-sm text-gray-700 mb-6">
          {print_lang('unsaved_chart_warning',
            'The tooth chart has marks that have not been saved. Leaving this screen will discard them.')}
        </p>
        <div className="flex justify-end gap-3">
          <button className="btn" onClick={() => setPendingNav(null)}>
            {print_lang('stay_and_save', 'Stay on this chart')}
          </button>
          <button className="btn btn-danger"
                  onClick={() => { const t = pendingNav; setPendingNav(null); runNav(t); }}>
            {print_lang('discard_and_continue', 'Discard and continue')}
          </button>
        </div>
      </div>
    </div>
  );

  // Clinical record fills the whole tablet screen; returning (rather than
  // overlaying) keeps the Odontogram's mounted state simple and leaves the
  // queue/appointment selection untouched underneath so Back can restore it.
  if (selectedPatient && showRecord) {
    return (
      <PatientRecord
        patient={selectedPatient}
        // The full branch row, not a two-field stub. It reaches
        // DrugLabelPrintModal -> printViaAgent(), which resolves the printer
        // from `branch.label_printer_name`: with the stub that column was
        // always undefined, so a clinic that had configured a label printer
        // silently got the browser print dialog on the tablet while the
        // desktop app printed straight to the printer, with nothing shown to
        // explain the difference. `branchSettings` is fetched on mount
        // (fetchBranchSettings) and already used for the smartcard toggle.
        // Fall back to the stub only while that fetch is still in flight or
        // has failed, so the record still opens with the branch id it needs.
        currentBranch={branchSettings || { branch_id: user?.branch_id, vat_rate: user?.vat_rate }}
        user={user}
        onBack={() => setShowRecord(false)}
      />
    );
  }

  // If patient selected, show treatment view
  if (selectedPatient) {
    return (
      <div className="space-y-6">
        {noticeBanner}
        {unsavedChartDialog}
        {/* Patient Header */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => requestNav('back')}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft size={24} />
              </button>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <User size={24} className="text-blue-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">{selectedPatient.name}</h2>
                <div className="text-sm text-gray-500 flex items-center gap-2">
                  <Phone size={14} />
                  {selectedPatient.phone || '-'}
                </div>
              </div>
            </div>

            {/* Medical Alerts */}
            <div className="flex flex-wrap gap-2">
              {selectedPatient.drug_allergy && (
                <div className="flex items-center gap-2 px-3 py-2 bg-red-100 text-red-700 rounded-lg">
                  <AlertTriangle size={18} />
                  <div>
                    <div className="text-xs font-medium">{print_lang('drug_allergy', 'Drug Allergy')}</div>
                    <div className="text-sm">{selectedPatient.drug_allergy}</div>
                  </div>
                </div>
              )}
              {selectedPatient.medical_history && (
                <div className="flex items-center gap-2 px-3 py-2 bg-orange-100 text-orange-700 rounded-lg">
                  <AlertCircle size={18} />
                  <div>
                    <div className="text-xs font-medium">{print_lang('medical_history', 'Medical History')}</div>
                    <div className="text-sm max-w-xs truncate">{selectedPatient.medical_history}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => requestNav('record')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
              >
                <FileText size={18} />
                {print_lang('clinical_record', 'Clinical Record')}
              </button>
              <button
                onClick={() => requestNav('finish')}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <CheckCircle size={18} />
                {print_lang('finish_treatment', 'Finish Treatment')}
              </button>
            </div>
          </div>
        </div>

        {/* Odontogram */}
        <Odontogram
          patientId={selectedPatient.patient_id}
          currentBranch={{ branch_id: user?.branch_id }}
          onDirtyChange={handleOdontogramDirty}
        />

        {/* Treatment Selection Modal */}
        {showTreatmentModal && (
          <div
            className="modal-overlay"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowTreatmentModal(false);
              }
            }}
          >
            <div
              className="modal-content w-full max-w-2xl p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold mb-6">{print_lang('select_treatments', 'Select Treatments Performed')}</h2>

              {/* Treatment List */}
              <div className="max-h-64 overflow-y-auto border rounded-lg mb-4">
                {treatments.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <p>{print_lang('no_treatments', 'No treatments available')}</p>
                    <p className="text-xs mt-2 text-red-500">
                      {print_lang('check_treatment_setup', 'Please check if treatments are configured for your clinic.')}
                    </p>
                    <button
                      type="button"
                      onClick={fetchTreatments}
                      className="mt-2 text-blue-600 hover:text-blue-800 text-sm underline"
                    >
                      {print_lang('retry', 'Retry')}
                    </button>
                  </div>
                ) : (
                  treatments.map(treatment => {
                    const isSelected = selectedTreatments.find(t => t.t_id === treatment.t_id);
                    return (
                      <div
                        key={treatment.t_id}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          // console.log('Treatment clicked:', treatment.name);
                          toggleTreatment(treatment);
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            toggleTreatment(treatment);
                          }
                        }}
                        className={`flex items-center justify-between p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors ${
                          isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => toggleTreatment(treatment)}
                            className="w-4 h-4 text-blue-600 rounded"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div>
                            <div className="font-medium">
                              {currentLanguage === 'th' ? (treatment.name_th || treatment.name) : treatment.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {currentLanguage === 'th' ? (treatment.category_name_th || treatment.category_name) : treatment.category_name}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-blue-600">{treatment.price?.toLocaleString()}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Notes */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {print_lang('treatment_notes', 'Treatment Notes')}
                </label>
                <textarea
                  value={treatmentNotes}
                  onChange={(e) => setTreatmentNotes(e.target.value)}
                  rows={3}
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={print_lang('enter_notes', 'Enter treatment notes...')}
                />
              </div>

              {/* Selected Summary */}
              {selectedTreatments.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4 mb-4">
                  <h3 className="font-medium text-blue-800 mb-2">{print_lang('selected_treatments', 'Selected Treatments')}</h3>
                  <ul className="space-y-1 text-sm">
                    {selectedTreatments.map(t => (
                      <li key={t.t_id} className="flex justify-between">
                        <span>{currentLanguage === 'th' ? (t.name_th || t.name) : t.name}</span>
                        <span className="font-medium">{t.price?.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 pt-3 border-t border-blue-200 flex justify-between font-bold text-blue-900">
                    <span>{print_lang('total', 'Total')}</span>
                    <span>{selectedTreatments.reduce((sum, t) => sum + (t.price || 0), 0).toLocaleString()}</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    // console.log('Cancel clicked');
                    setShowTreatmentModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  style={{ cursor: 'pointer' }}
                >
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // console.log('Complete Treatment clicked, selected:', selectedTreatments.length);
                    handleFinishTreatment();
                  }}
                  disabled={isSaving || selectedTreatments.length === 0}
                  className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  style={{ cursor: selectedTreatments.length === 0 ? 'not-allowed' : 'pointer' }}
                >
                  <CheckCircle size={18} />
                  {isSaving ? print_lang('saving', 'Saving...') : print_lang('complete_treatment', 'Complete Treatment')}
                </button>
              </div>
              {selectedTreatments.length === 0 && (
                <p className="text-sm text-orange-600 text-center mt-2">
                  {print_lang('select_treatment_first', '* Please select at least one treatment above')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Queue Dashboard View
  return (
    <div className="space-y-6">
      {noticeBanner}
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users size={28} />
            {print_lang('waiting_list', 'Waiting List')}
          </h1>
          {/* Current Branch Indicator */}
          <p className="text-sm text-gray-500 mt-1">
            {print_lang('current_branch', 'Branch')}: {user?.branches?.find(b => b.branch_id === user.branch_id)?.branch_name || `ID: ${user?.branch_id}`}
            {user?.branches?.length > 1 && (
              <span className="ml-2 text-blue-600">
                ({print_lang('switch_in_sidebar', 'Switch branch in sidebar')})
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">
            {print_lang('last_update', 'Last update')}: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            onClick={openWalkInModal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
          >
            <UserPlus size={18} />
            {print_lang('walk_in_patient', 'Walk-in')}
          </button>
          <button
            onClick={fetchQueue}
            disabled={isLoading}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-yellow-600 text-sm font-medium">{print_lang('waiting', 'Waiting')}</div>
          <div className="text-3xl font-bold text-yellow-700">{stats.waiting}</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-blue-600 text-sm font-medium">{print_lang('in_progress', 'In Progress')}</div>
          <div className="text-3xl font-bold text-blue-700">{stats.inProgress}</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="text-gray-600 text-sm font-medium">{print_lang('scheduled', 'Scheduled')}</div>
          <div className="text-3xl font-bold text-gray-700">{stats.scheduled}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-green-600 text-sm font-medium">{print_lang('total_today', 'Total Today')}</div>
          <div className="text-3xl font-bold text-green-700">{stats.total}</div>
        </div>
      </div>

      {/* Queue List */}
      <div className="bg-white rounded-lg shadow">
        {isLoading && queue.length === 0 ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : queue.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Users size={48} className="mx-auto mb-4 text-gray-300" />
            <p>{print_lang('no_patients_waiting', 'No patients in queue')}</p>
          </div>
        ) : (
          <div className="divide-y">
            {queue.map(appointment => {
              const statusBadge = getStatusBadge(appointment.status);
              const StatusIcon = statusBadge.icon;

              return (
                <div
                  key={appointment.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${
                    appointment.status === 'in_progress' ? 'bg-blue-50 border-l-4 border-l-blue-500' :
                    appointment.status === 'checked_in' ? 'bg-yellow-50 border-l-4 border-l-yellow-500' : ''
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    {/* Patient Info */}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                        <User size={24} className="text-gray-500" />
                      </div>
                      <div>
                        <div className="font-bold text-lg text-gray-800">{appointment.patient_name}</div>
                        <div className="text-sm text-gray-500 flex flex-wrap items-center gap-3">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {formatTime(appointment.start_date)}
                          </span>
                          {appointment.patient_phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={14} />
                              {appointment.patient_phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Appointment Type & Alerts */}
                    <div className="flex flex-wrap items-center gap-2">
                      {appointment.type_name && (
                        <span
                          className="px-3 py-1 rounded-full text-sm font-medium text-white"
                          style={{ backgroundColor: appointment.color || '#6b7280' }}
                        >
                          {currentLanguage === 'th' ? (appointment.type_name_th || appointment.type_name) : appointment.type_name}
                        </span>
                      )}

                      {appointment.drug_allergy && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                          <AlertTriangle size={14} />
                          {print_lang('allergy', 'Allergy')}
                        </span>
                      )}

                      {appointment.medical_history && (
                        <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                          <FileText size={14} />
                          {print_lang('history', 'History')}
                        </span>
                      )}
                    </div>

                    {/* Status & Actions */}
                    <div className="flex flex-wrap items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${statusBadge.bg} ${statusBadge.text}`}>
                        <StatusIcon size={14} />
                        {print_lang(appointment.status, appointment.status)}
                      </span>

                      {appointment.status === 'scheduled' && (
                        <button
                          onClick={() => updateStatus(appointment.id, 'checked_in')}
                          className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm font-medium"
                        >
                          {print_lang('check_in', 'Check In')}
                        </button>
                      )}

                      {(appointment.status === 'checked_in' || appointment.status === 'in_progress') && (
                        <button
                          onClick={() => handleStartTreatment(appointment)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
                        >
                          <Play size={16} />
                          {appointment.status === 'in_progress'
                            ? print_lang('continue', 'Continue')
                            : print_lang('start_treatment', 'Start Treatment')}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  {appointment.text && (
                    <div className="mt-2 ml-16 text-sm text-gray-500 italic">
                      {appointment.text}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Walk-in Patient Modal */}
      {showWalkInModal && (
        <div className="modal-overlay" onClick={closeWalkInModal}>
          <div className="modal-content w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <UserPlus size={24} className="text-blue-600" />
                {print_lang('walk_in_patient', 'Walk-in Patient')}
              </h2>
              <button onClick={closeWalkInModal} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            {/* Patient Search or New Patient Form */}
            {!showNewPatientForm ? (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">
                    {print_lang('select_patient', 'Select Patient')} <span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowNewPatientForm(true)}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    <UserPlus size={16} />
                    {print_lang('new_patient', 'New Patient')}
                  </button>
                </div>
                <div className="relative mb-2">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={patientSearch}
                    onChange={(e) => {
                      setPatientSearch(e.target.value);
                      fetchPatients(e.target.value);
                    }}
                    placeholder={print_lang('search_patient', 'Search patient by name or phone...')}
                    className="w-full pl-10 border rounded-lg px-3 py-2"
                  />
                </div>
                <select
                  value={walkInForm.patient_id}
                  onChange={(e) => setWalkInForm(prev => ({ ...prev, patient_id: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">{print_lang('select_patient', '-- Select Patient --')}</option>
                  {patients.map(patient => (
                    <option key={patient.patient_id} value={patient.patient_id}>
                      {patient.name} {patient.phone ? `(${patient.phone})` : ''}
                    </option>
                  ))}
                </select>
                {walkInForm.patient_id && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-sm">
                    {(() => {
                      const p = patients.find(pt => pt.patient_id === parseInt(walkInForm.patient_id));
                      return p ? (
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-blue-600" />
                          <span className="font-medium">{p.name}</span>
                          {p.drug_allergy && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs flex items-center gap-1">
                              <AlertTriangle size={12} /> {print_lang('allergy', 'Allergy')}
                            </span>
                          )}
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            ) : (
              /* New Patient Registration Form */
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-green-800 flex items-center gap-2">
                    <UserPlus size={18} />
                    {print_lang('register_new_patient', 'Register New Patient')}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowNewPatientForm(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Read SmartCard Button */}
                  {branchSettings?.smartcard_enabled === 1 && (
                    <button
                      type="button"
                      onClick={handleReadSmartcard}
                      disabled={isReadingCard}
                      className="w-full py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <CreditCard size={18} />
                      {isReadingCard
                        ? print_lang('reading_smartcard', 'Reading SmartCard...')
                        : print_lang('read_smartcard', 'Read SmartCard')
                      }
                    </button>
                  )}

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('name', 'Name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={newPatientForm.name}
                      onChange={(e) => setNewPatientForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder={print_lang('patient_name', 'Patient name')}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Phone & Citizen ID */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('phone', 'Phone')}
                      </label>
                      <input
                        type="tel"
                        value={newPatientForm.phone}
                        onChange={(e) => setNewPatientForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="0812345678"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('citizen_id', 'Citizen ID')}
                      </label>
                      <input
                        type="text"
                        value={newPatientForm.citizen_id}
                        onChange={(e) => setNewPatientForm(prev => ({ ...prev, citizen_id: e.target.value }))}
                        placeholder="1234567890123"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  {/* Gender & DOB */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('gender', 'Gender')}
                      </label>
                      <select
                        value={newPatientForm.gender}
                        onChange={(e) => setNewPatientForm(prev => ({ ...prev, gender: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        <option value="">{print_lang('select_gender', '-- Select --')}</option>
                        <option value="male">{print_lang('male', 'Male')}</option>
                        <option value="female">{print_lang('female', 'Female')}</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {print_lang('dob', 'Date of Birth')}
                      </label>
                      <input
                        type="date"
                        value={newPatientForm.dob}
                        onChange={(e) => setNewPatientForm(prev => ({ ...prev, dob: e.target.value }))}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      {print_lang('address', 'Address')}
                    </label>
                    <input
                      type="text"
                      value={newPatientForm.address}
                      onChange={(e) => setNewPatientForm(prev => ({ ...prev, address: e.target.value }))}
                      placeholder={print_lang('address_placeholder', 'Address from ID card')}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Drug Allergy */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-red-600">
                      {print_lang('drug_allergy', 'Drug Allergy')}
                    </label>
                    <input
                      type="text"
                      value={newPatientForm.drug_allergy}
                      onChange={(e) => setNewPatientForm(prev => ({ ...prev, drug_allergy: e.target.value }))}
                      placeholder={print_lang('drug_allergy_placeholder', 'e.g., Penicillin, Aspirin')}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Medical History */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-orange-600">
                      {print_lang('medical_history', 'Medical History')}
                    </label>
                    <input
                      type="text"
                      value={newPatientForm.medical_history}
                      onChange={(e) => setNewPatientForm(prev => ({ ...prev, medical_history: e.target.value }))}
                      placeholder={print_lang('medical_history_placeholder', 'e.g., Diabetes, Hypertension')}
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  {/* Register Button */}
                  <button
                    type="button"
                    onClick={createNewPatient}
                    disabled={isCreatingPatient || !newPatientForm.name.trim()}
                    className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <CheckCircle size={18} />
                    {isCreatingPatient
                      ? print_lang('registering', 'Registering...')
                      : print_lang('register_and_select', 'Register & Select')
                    }
                  </button>
                </div>
              </div>
            )}

            {/* Appointment Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                {print_lang('appointment_type', 'Appointment Type')}
              </label>
              <select
                value={walkInForm.evtype_id}
                onChange={(e) => setWalkInForm(prev => ({ ...prev, evtype_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">{print_lang('general_checkup', 'General Checkup')}</option>
                {appointmentTypes.map(type => (
                  <option key={type.evtype_id} value={type.evtype_id}>
                    {currentLanguage === 'th' ? (type.type_name_th || type.type_name) : type.type_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                {print_lang('notes', 'Notes')}
              </label>
              <textarea
                value={walkInForm.notes}
                onChange={(e) => setWalkInForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder={print_lang('walk_in_notes_placeholder', 'Chief complaint or reason for visit...')}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={closeWalkInModal}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {print_lang('cancel', 'Cancel')}
              </button>
              <button
                onClick={createWalkInAppointment}
                disabled={isCreatingWalkIn || !walkInForm.patient_id}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <UserPlus size={18} />
                {isCreatingWalkIn
                  ? print_lang('adding', 'Adding...')
                  : print_lang('add_to_queue', 'Add to Queue')
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaitingList;
